import { useState } from "react";
import { Alert, Button, Checkbox, Modal } from "../../ds";
import type { Backup, DeviceDetail } from "../../lib/api";
import { fmtDateTime } from "../../lib/format";
import { KeyValues } from "../KeyValues";

// Bestätigungsdialog fürs Einspielen eines Backups. Der gefährliche Teil ist
// nicht der Klick, sondern die Kompatibilität: ein Config-Tar aus einer älteren
// Firmware oder von einem anderen Modell kann den Router unbrauchbar machen —
// und es gibt kein Rollback (docs/device-management.md → „Backups“).

/** Die beiden Fälle, die eine explizite Zusatzbestätigung erzwingen. */
const isRisky = (backup: Backup): boolean =>
  backup.compat.firmware === "older" || !backup.compat.modelMatch;

const FIRMWARE_NOTE: Record<Backup["compat"]["firmware"], string> = {
  same: "gleiche Firmware",
  older: "älter als die laufende Firmware",
  newer: "neuer als die laufende Firmware",
  unknown: "Stand unbekannt",
};

function CompatWarnings({ backup, device }: { backup: Backup; device: DeviceDetail }) {
  const { firmware, modelMatch, sameDevice } = backup.compat;
  return (
    <>
      {firmware === "older" && (
        <Alert variant="danger" title="Ältere Firmware — kein Rollback">
          Das Backup stammt aus <strong>{backup.firmwareVersion ?? "unbekannter Firmware"}</strong>,
          auf dem Router läuft <strong>{device.firmwareVersion ?? "unbekannt"}</strong>. Zwischen
          Firmware-Ständen ändern sich UCI-Schemata: eine alte Konfiguration kann den Router
          unbrauchbar machen, und ein automatisches Zurück gibt es nicht. Danach hilft nur noch eine
          Fahrt vor Ort.
        </Alert>
      )}
      {firmware === "newer" && (
        <Alert variant="warning">
          Das Backup ist aus einer <strong>neueren</strong> Firmware (
          {backup.firmwareVersion ?? "unbekannt"}) als die laufende (
          {device.firmwareVersion ?? "unbekannt"}). Neue Optionen kennt der Router womöglich nicht.
        </Alert>
      )}
      {!modelMatch && (
        <Alert variant="danger" title="Anderes Modell">
          Das Backup wurde auf <strong>{backup.model ?? "unbekanntem Modell"}</strong> erstellt,
          dieser Router ist ein <strong>{device.model ?? "unbekanntes Modell"}</strong>. Fremde
          Modelle haben andere Interfaces — Netzwerk und Firewall passen danach mit hoher
          Wahrscheinlichkeit nicht mehr.
        </Alert>
      )}
      {!sameDevice && modelMatch && (
        <Alert variant="warning">
          Das Backup stammt von einem anderen Router (
          <strong>{backup.deviceHostname ?? backup.deviceId}</strong>). Hostname,
          WireGuard-Schlüssel und Adressen aus diesem Backup ersetzen die des Ziel-Routers.
        </Alert>
      )}
      {firmware === "unknown" && (
        <Alert variant="warning">
          Zu diesem Backup ist keine Firmware-Version hinterlegt — die Kompatibilität lässt sich
          nicht prüfen.
        </Alert>
      )}
    </>
  );
}

export function BackupRestoreModal({
  backup,
  device,
  busy,
  onClose,
  onConfirm,
}: {
  backup: Backup | null;
  device: DeviceDetail;
  busy: boolean;
  onClose: () => void;
  onConfirm: () => void;
}) {
  // Der Aufrufer hängt die `id` des Backups als React-`key` daran: ein anderes
  // Backup ist eine andere Instanz und damit unbestätigt. Sonst trüge ein
  // Häkchen aus dem vorigen Dialog in den nächsten.
  const [acknowledged, setAcknowledged] = useState(false);

  if (!backup) return null;
  const risky = isRisky(backup);

  return (
    <Modal
      open
      onClose={onClose}
      title="Backup einspielen?"
      subtitle={`Der Router lädt das Archiv selbst, prüft die Prüfsumme und startet danach neu.`}
      actions={
        <>
          <Button variant="ghost" onClick={onClose}>
            Abbrechen
          </Button>
          <Button
            variant="danger"
            iconName="upload"
            disabled={risky && !acknowledged}
            loading={busy}
            onClick={onConfirm}
          >
            Einspielen
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <KeyValues
          items={[
            ["Backup vom", fmtDateTime(backup.createdAt)],
            ["Quelle", backup.deviceHostname ?? backup.deviceId],
            [
              "Firmware im Backup",
              `${backup.firmwareVersion ?? "—"} (${FIRMWARE_NOTE[backup.compat.firmware]})`,
            ],
            ["Firmware auf dem Router", device.firmwareVersion ?? "unbekannt"],
            ["Modell im Backup", backup.model ?? "—"],
            ["Modell des Routers", device.model ?? "—"],
          ]}
        />

        <CompatWarnings backup={backup} device={device} />

        {risky && (
          <Checkbox
            label="Ich weiß, dass das den Router unbrauchbar machen kann"
            description="Es gibt kein Rollback. Ohne dieses Häkchen bleibt „Einspielen“ gesperrt."
            checked={acknowledged}
            onChange={(e) => setAcknowledged(e.target.checked)}
          />
        )}
      </div>
    </Modal>
  );
}
